// Schedules (on-call rotations) list + minimal CRUD. Admin-only.
//
// This page is the chunk-11-PR1 holding pattern: bare-bones rows
// showing each schedule's current on-call user and a create form. The
// calendar-timeline visualisation lands in PR 2; the override flow in
// PR 3. Both will share this page's URL and just enrich the rendering.

import { useEffect, useMemo, useState } from 'react';
import { onSnapshot, orderBy, query } from 'firebase/firestore';
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  ShieldAlert,
  ShieldOff,
  Trash2,
  Users,
} from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import { orgSchedulesCol, orgMembersCol, orgTeamsCol } from '../lib/firestore';
import {
  SHIFT_LENGTH_PRESETS,
  clearScheduleOverride,
  colourForUser,
  createSchedule,
  deleteSchedule,
  effectiveOverrides,
  resolveOnCallUser,
  updateSchedule,
} from '../lib/schedules';
import type { OrgMember, Schedule, Team } from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';
import { ScheduleTimeline } from '../components/schedules/ScheduleTimeline';
import {
  OverrideDialog,
  type OverridePrefill,
} from '../components/schedules/OverrideDialog';

export default function SchedulesPage() {
  const { orgId } = useOrg();
  const [schedules, setSchedules] = useState<Schedule[] | null>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Re-render once a minute so "currently on call" and "next handover"
  // tick across shift boundaries without a manual refresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!orgId) return;
    const unsubSched = onSnapshot(
      query(orgSchedulesCol(orgId), orderBy('createdAt', 'asc')),
      (snap) => {
        setSchedules(snap.docs.map((d) => d.data() as Schedule));
      },
      (err) => {
        console.error('[schedules] snapshot failed', err);
        setError('Could not load schedules.');
      },
    );
    const unsubMembers = onSnapshot(
      query(orgMembersCol(orgId), orderBy('joinedAt', 'asc')),
      (snap) => {
        setMembers(snap.docs.map((d) => d.data() as OrgMember));
      },
    );
    const unsubTeams = onSnapshot(
      query(orgTeamsCol(orgId), orderBy('name', 'asc')),
      (snap) => {
        setTeams(snap.docs.map((d) => d.data() as Team));
      },
    );
    return () => {
      unsubSched();
      unsubMembers();
      unsubTeams();
    };
  }, [orgId]);

  async function onDelete() {
    if (!confirmDeleteId || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteSchedule(orgId!, confirmDeleteId);
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('[schedules] delete failed', err);
      setError(extractMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  if (!orgId) return null;

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title="On-call rotations"
          subtitle="Define who's on call when. Reference a rotation from any escalation policy as a 'schedule' target — incidents page the person whose shift covers the moment of trigger."
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={6}>
          {showForm ? (
            <ScheduleForm
              orgId={orgId}
              members={members}
              teams={teams}
              onCancel={() => setShowForm(false)}
              onSaved={() => setShowForm(false)}
              onError={(m) => setError(m)}
            />
          ) : (
            <Row justify="end">
              <Button
                variant="primary"
                size="sm"
                iconLeft={<Plus size={12} strokeWidth={2} />}
                onClick={() => setShowForm(true)}
              >
                New rotation
              </Button>
            </Row>
          )}

          {schedules === null ? (
            <Row gap={2} align="center">
              <Spinner size="sm" />
              <span className="ds-text-muted ds-text-sm">Loading…</span>
            </Row>
          ) : schedules.length === 0 ? (
            <Card>
              <CardBody>
                <Row gap={3} align="center">
                  <Calendar size={20} strokeWidth={1.75} color="var(--color-text-tertiary)" />
                  <Stack gap={1}>
                    <span style={{ fontWeight: 'var(--fw-medium)' }}>No rotations yet</span>
                    <span className="ds-text-muted ds-text-sm">
                      Create your first rotation, then reference it from an escalation policy step
                      as a &quot;schedule&quot; target.
                    </span>
                  </Stack>
                </Row>
              </CardBody>
            </Card>
          ) : (
            <Stack gap={3}>
              {schedules.map((s) => (
                <ScheduleRow
                  key={s.id}
                  schedule={s}
                  members={members}
                  teams={teams}
                  orgId={orgId}
                  onDelete={() => setConfirmDeleteId(s.id)}
                  onError={(m) => setError(m)}
                />
              ))}
            </Stack>
          )}
        </Stack>

        <ConfirmDialog
          open={confirmDeleteId !== null}
          onConfirm={onDelete}
          onCancel={() => setConfirmDeleteId(null)}
          title="Delete this rotation?"
          confirmLabel="Delete"
          destructive
          loading={deleting}
        >
          <p>
            Any escalation policy step that references this rotation will fail to route at trigger
            time. Make sure no policy still depends on it before deleting.
          </p>
        </ConfirmDialog>
      </Container>
    </AppShell>
  );
}

/**
 * Default visible window: today − 1 day → today + 13 days. The "−1
 * day" gives one trailing day of context for very recent handovers.
 */
const DEFAULT_WINDOW_DAYS = 14;
const PAN_DAYS = 7;

function ScheduleRow({
  schedule,
  members,
  teams,
  orgId,
  onDelete,
  onError,
}: {
  schedule: Schedule;
  members: OrgMember[];
  teams: Team[];
  orgId: string;
  onDelete: () => void;
  onError: (m: string) => void;
}) {
  const now = Date.now();
  const resolution = resolveOnCallUser(schedule, now);
  const totalUsers = schedule.userIds?.length ?? 0;
  // Pending = still-relevant overrides (haven't ended yet). Active is
  // a subset whose window contains `now`. The "Override now" button
  // hides when there's an active one to avoid a confusing
  // double-cover; it shows again once the active one ends or you've
  // got only future ones queued.
  const pendingOverrides = effectiveOverrides(schedule).filter(
    (o) => o.untilMs > now,
  );
  const hasActiveOverride = pendingOverrides.some(
    (o) => o.fromMs <= now && o.untilMs > now,
  );

  // Override modal state — opened from two places:
  //   1. Clicking a future shift on the timeline (pre-fill the
  //      window with that shift's bounds)
  //   2. The "Override now" button below (pre-fill with [now,
  //      end of current shift])
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overridePrefill, setOverridePrefill] = useState<OverridePrefill | null>(null);
  // When true, the inline edit form replaces the row's normal content
  // until the admin saves or cancels.
  const [editing, setEditing] = useState(false);

  function openOverrideFromSlot(slot: { startMs: number; endMs: number; displayName: string }) {
    setOverridePrefill({
      fromMs: slot.startMs,
      untilMs: slot.endMs,
      replacingUserDisplayName: slot.displayName,
    });
    setOverrideOpen(true);
  }
  function openOverrideNow() {
    setOverridePrefill({
      fromMs: Date.now(),
      // Default until = end of current rotation slot, falling back to
      // +24h if we can't resolve one.
      untilMs:
        resolution?.endsAtMs ?? Date.now() + 24 * 60 * 60 * 1000,
      replacingUserDisplayName:
        resolution?.source === 'rotation' ? resolution.displayName : undefined,
    });
    setOverrideOpen(true);
  }
  // Track which specific override is being cleared so its button can
  // show a loading spinner while others stay clickable.
  const [clearingId, setClearingId] = useState<string | null>(null);
  // Override list defaults to collapsed — the row header carries the
  // summary so the section stays compact when an admin has stacked
  // up several covers. Click the header to expand.
  const [overridesExpanded, setOverridesExpanded] = useState(false);
  async function onClearOverride(overrideId: string) {
    setClearingId(overrideId);
    try {
      await clearScheduleOverride(orgId, schedule.id, overrideId);
    } catch (err) {
      console.error('[override] clear failed', err);
      onError(extractMessage(err));
    } finally {
      setClearingId(null);
    }
  }

  // Per-row visible window. Each row pans independently so admins can
  // jog forward through future shifts on one rotation without
  // disturbing the others.
  const [windowFromMs, setWindowFromMs] = useState(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return start.getTime() - 24 * 60 * 60 * 1000;
  });
  const windowToMs = windowFromMs + DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000;

  // When editing, render the form in-place inside the row so the
  // admin doesn't lose context of which schedule they're amending.
  if (editing) {
    return (
      <Card>
        <CardBody>
          <ScheduleForm
            orgId={orgId}
            members={members}
            teams={teams}
            editing={schedule}
            onCancel={() => setEditing(false)}
            onSaved={() => setEditing(false)}
            onError={onError}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <Stack gap={3}>
          <Row gap={3} justify="between" align="start">
            <Stack gap={1}>
              <Row gap={2} align="center">
                <Calendar size={14} strokeWidth={2} color="var(--color-accent)" />
                <span style={{ fontWeight: 'var(--fw-semibold)' }}>{schedule.name}</span>
                {schedule.teamName && (
                  <span className="ds-text-muted ds-text-sm">
                    · {schedule.teamName}
                  </span>
                )}
              </Row>
              <span className="ds-text-muted ds-text-sm">
                {totalUsers} {totalUsers === 1 ? 'user' : 'users'} ·{' '}
                {schedule.shiftLengthHours >= 24
                  ? `${schedule.shiftLengthHours / 24}-day shifts`
                  : `${schedule.shiftLengthHours}-hour shifts`}
              </span>
            </Stack>
            <Row gap={1} align="center">
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Pencil size={12} strokeWidth={2} />}
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
              <Button
                variant="danger-ghost"
                size="sm"
                iconLeft={<Trash2 size={12} strokeWidth={2} />}
                onClick={onDelete}
              >
                Delete
              </Button>
            </Row>
          </Row>

          {resolution ? (
            <Row gap={3} justify="between" align="center">
              <Row gap={2} align="center">
                <span
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: colourForUser(resolution.userId),
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontWeight: 'var(--fw-medium)' }}>
                  Currently on call: {resolution.displayName}
                </span>
                {resolution.source === 'override' && (
                  <Badge tone="warning">Override</Badge>
                )}
                <span className="ds-text-muted ds-text-sm">
                  ·{' '}
                  {resolution.source === 'override'
                    ? `until ${formatDatetime(resolution.endsAtMs)}`
                    : `next handover ${formatDatetime(resolution.endsAtMs)}`}
                </span>
              </Row>
              {/* "Override now" button is always available — multiple
                  non-overlapping overrides can coexist. Clearing
                  happens per-override in the list below. */}
              {totalUsers > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<ShieldAlert size={12} strokeWidth={2} />}
                  onClick={openOverrideNow}
                  disabled={hasActiveOverride}
                  title={
                    hasActiveOverride
                      ? 'An override is currently active — schedule another for a different window'
                      : undefined
                  }
                >
                  Override now
                </Button>
              )}
            </Row>
          ) : (
            <span className="ds-text-tertiary ds-text-sm">
              <Users size={12} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} />{' '}
              No users in the rotation — edit the schedule to add some.
            </span>
          )}

          {/* Pending overrides — collapsible. The header summarises
              count + the currently-active cover (if any) so it stays
              informative when closed. Click to expand the full list
              with per-override Clear buttons. Default-closed so a
              row with several covers doesn't dominate the page. */}
          {pendingOverrides.length > 0 && (
            <OverridesSection
              overrides={pendingOverrides}
              now={now}
              expanded={overridesExpanded}
              onToggle={() => setOverridesExpanded((v) => !v)}
              onClear={onClearOverride}
              clearingId={clearingId}
            />
          )}

          {totalUsers > 0 && (
            <Stack gap={2}>
              <Row gap={2} align="center" justify="between">
                <span className="ds-label">Coverage</span>
                <Row gap={1} align="center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setWindowFromMs((ms) => ms - PAN_DAYS * 24 * 60 * 60 * 1000)
                    }
                    aria-label="Pan timeline left"
                  >
                    <ChevronLeft size={12} strokeWidth={2} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const start = new Date();
                      start.setHours(0, 0, 0, 0);
                      setWindowFromMs(start.getTime() - 24 * 60 * 60 * 1000);
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setWindowFromMs((ms) => ms + PAN_DAYS * 24 * 60 * 60 * 1000)
                    }
                    aria-label="Pan timeline right"
                  >
                    <ChevronRight size={12} strokeWidth={2} />
                  </Button>
                </Row>
              </Row>
              <ScheduleTimeline
                schedule={schedule}
                fromMs={windowFromMs}
                toMs={windowToMs}
                onSlotClick={(slot) => openOverrideFromSlot(slot)}
              />
              {/* Rotation order badges below the timeline give the at-
                  a-glance "who's in what slot" reference. Active slot
                  highlighted. */}
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--space-1)',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {schedule.userIds.map((uid, i) => (
                  <Badge
                    key={uid}
                    tone={
                      resolution &&
                      resolution.source === 'rotation' &&
                      resolution.rotationIdx === i
                        ? 'accent'
                        : 'neutral'
                    }
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: colourForUser(uid),
                        marginRight: 6,
                      }}
                      aria-hidden
                    />
                    {schedule.userDisplayNames?.[uid] ?? uid}
                  </Badge>
                ))}
              </div>
            </Stack>
          )}
        </Stack>
      </CardBody>
      <OverrideDialog
        open={overrideOpen}
        schedule={schedule}
        orgId={orgId}
        members={members}
        prefill={overridePrefill}
        onClose={() => setOverrideOpen(false)}
        onSaved={() => {
          setOverrideOpen(false);
          setOverridePrefill(null);
        }}
      />
    </Card>
  );
}

/**
 * Form for creating OR editing a rotation. Same fields either way —
 * passing `editing` switches the submit path to updateSchedule and
 * pre-fills the state from the existing doc. Single component keeps
 * the field rendering and member-picker logic DRY.
 */
/**
 * Collapsible override list for a schedule row. Header line summarises
 * the count + the currently-active cover (if any) so the section is
 * informative even when closed. Expanded content shows full details
 * per override with per-row Clear buttons.
 */
function OverridesSection({
  overrides,
  now,
  expanded,
  onToggle,
  onClear,
  clearingId,
}: {
  overrides: Schedule['overrides'] extends infer T
    ? T extends Array<infer Item>
      ? Item[]
      : never
    : never;
  now: number;
  expanded: boolean;
  onToggle: () => void;
  onClear: (overrideId: string) => void;
  clearingId: string | null;
}) {
  const active = overrides.find((o) => o.fromMs <= now && o.untilMs > now);
  const scheduledCount = overrides.length - (active ? 1 : 0);

  // Summary text drives the closed-state header — keeps the
  // information density high without taking many lines.
  const summary =
    active && scheduledCount > 0
      ? `${active.userDisplayName ?? active.userId} covering until ${formatDatetime(active.untilMs)} · ${scheduledCount} more scheduled`
      : active
        ? `${active.userDisplayName ?? active.userId} covering until ${formatDatetime(active.untilMs)}`
        : `${overrides.length} scheduled`;

  return (
    <div
      style={{
        background: 'var(--color-warning-soft)',
        border: '1px solid rgba(245,158,11,0.4)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-4)',
          background: 'transparent',
          border: 'none',
          color: 'var(--color-warning-text)',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 'var(--fs-sm)',
        }}
      >
        <ShieldAlert size={12} strokeWidth={2} />
        <span style={{ fontWeight: 'var(--fw-medium)' }}>
          {overrides.length} {overrides.length === 1 ? 'override' : 'overrides'}
        </span>
        <span style={{ color: 'var(--color-text-secondary)', flex: 1 }}>
          · {summary}
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          style={{
            transition: 'transform 120ms ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {overrides.map((o) => {
            const isActive = o.fromMs <= now && o.untilMs > now;
            return (
              <div
                key={o.id}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  borderTop: '1px solid rgba(245,158,11,0.3)',
                }}
              >
                <Row gap={3} justify="between" align="center">
                  <Stack gap={1}>
                    <Row gap={2} align="center">
                      <span
                        className="ds-text-sm"
                        style={{
                          fontWeight: 'var(--fw-medium)',
                          color: 'var(--color-warning-text)',
                        }}
                      >
                        {o.userDisplayName ?? o.userId} covering
                        {o.fromMs > now ? ' from ' : ' '}
                        {o.fromMs > now ? formatDatetime(o.fromMs) : ''}
                        {o.fromMs > now ? ' until ' : 'until '}
                        {formatDatetime(o.untilMs)}
                      </span>
                      {!isActive && <Badge tone="neutral">Scheduled</Badge>}
                    </Row>
                    {o.reason && (
                      <span className="ds-text-muted ds-text-xs">
                        Reason: {o.reason}
                      </span>
                    )}
                    <span className="ds-text-tertiary ds-text-xs">
                      Set by {o.setByDisplayName ?? o.setByUserId} ·{' '}
                      {formatDatetime(o.setAt)}
                    </span>
                  </Stack>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<ShieldOff size={12} strokeWidth={2} />}
                    onClick={() => onClear(o.id)}
                    loading={clearingId === o.id}
                    disabled={clearingId !== null && clearingId !== o.id}
                  >
                    Clear
                  </Button>
                </Row>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScheduleForm({
  orgId,
  members,
  teams,
  editing,
  onCancel,
  onSaved,
  onError,
}: {
  orgId: string;
  members: OrgMember[];
  teams: Team[];
  /** When set, the form runs in "edit" mode and updates this schedule. */
  editing?: Schedule | null;
  onCancel: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const isEdit = Boolean(editing);
  const [name, setName] = useState(editing?.name ?? '');
  const [teamId, setTeamId] = useState(editing?.teamId ?? '');
  const [shiftLengthHours, setShiftLengthHours] = useState(
    editing?.shiftLengthHours ?? 168,
  );
  const [startAtLocal, setStartAtLocal] = useState(() =>
    editing ? toLocalInputIso(editing.startAt) : defaultStartAtLocalIso(),
  );
  // Ordered list of selected user IDs. We render the order so admins
  // can see who's first / second / third in the rotation cycle.
  const [selected, setSelected] = useState<string[]>(editing?.userIds ?? []);
  const [submitting, setSubmitting] = useState(false);

  const sortedTeams = useMemo(
    () => [...teams].sort((a, b) => a.name.localeCompare(b.name)),
    [teams],
  );

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
    [members],
  );
  const available = sortedMembers.filter((m) => !selected.includes(m.userId));

  const canSubmit =
    name.trim().length > 0 && selected.length > 0 && startAtLocal.length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const startAt = new Date(startAtLocal).getTime();
      if (!Number.isFinite(startAt)) {
        onError('Invalid start time.');
        setSubmitting(false);
        return;
      }
      if (isEdit && editing) {
        // Pass teamId verbatim — backend reads empty string as
        // "unassign" and a real id as "bind". Skipping the field
        // (undefined) means "no change" but we always want the form's
        // value to win on save.
        await updateSchedule(orgId, editing.id, {
          name: name.trim(),
          userIds: selected,
          shiftLengthHours,
          startAt,
          teamId,
        });
      } else {
        // On create, empty string means "create without team binding"
        // — the backend already treats falsy teamId that way.
        await createSchedule(orgId, {
          name: name.trim(),
          userIds: selected,
          shiftLengthHours,
          startAt,
          teamId: teamId || undefined,
        });
      }
      onSaved();
      if (!isEdit) {
        setName('');
        setSelected([]);
      }
    } catch (err) {
      console.error('[schedules] save failed', err);
      onError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function addMember(uid: string) {
    setSelected((cur) => [...cur, uid]);
  }
  function removeMember(uid: string) {
    setSelected((cur) => cur.filter((u) => u !== uid));
  }
  function moveMember(uid: string, direction: -1 | 1) {
    setSelected((cur) => {
      const idx = cur.indexOf(uid);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= cur.length) return cur;
      const next = [...cur];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  return (
    <Card>
      <CardBody>
        <Stack gap={4} as="form" onSubmit={submit}>
          <Row gap={2} align="center">
            <Calendar size={14} strokeWidth={2} color="var(--color-accent)" />
            <span style={{ fontWeight: 'var(--fw-medium)' }}>
              {isEdit ? 'Edit rotation' : 'New rotation'}
            </span>
          </Row>

          <FormField label="Name" hint="What this rotation covers — e.g. 'Platform on-call'.">
            {(id) => (
              <Input
                id={id}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Platform on-call"
                maxLength={100}
                autoFocus
                required
              />
            )}
          </FormField>

          <FormField
            label="Team (optional)"
            hint="Bind this rotation to a team. Used so escalation policies can target the team and resolve through this schedule."
          >
            {(id) => (
              <select
                id={id}
                className="ds-input"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
              >
                <option value="">Unassigned</option>
                {sortedTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </FormField>

          <FormField label="Shift length">
            {(id) => (
              <select
                id={id}
                className="ds-input"
                value={shiftLengthHours}
                onChange={(e) => setShiftLengthHours(Number(e.target.value))}
              >
                {SHIFT_LENGTH_PRESETS.map((p) => (
                  <option key={p.hours} value={p.hours}>
                    {p.label}
                  </option>
                ))}
              </select>
            )}
          </FormField>

          <FormField
            label="Rotation starts"
            hint="When the first user's first shift begins. Subsequent shifts roll forward from here. UTC."
          >
            {(id) => (
              <Input
                id={id}
                type="datetime-local"
                value={startAtLocal}
                onChange={(e) => setStartAtLocal(e.target.value)}
                required
              />
            )}
          </FormField>

          <Stack gap={2}>
            <span className="ds-label">Rotation order ({selected.length} selected)</span>
            {selected.length === 0 ? (
              <span className="ds-text-tertiary ds-text-sm">
                Add users below in the order they should cycle through.
              </span>
            ) : (
              <Stack gap={1}>
                {selected.map((uid, i) => {
                  const member = members.find((m) => m.userId === uid);
                  const label = member?.displayName ?? member?.email ?? uid;
                  return (
                    <Row
                      key={uid}
                      gap={2}
                      align="center"
                    >
                      <span
                        className="ds-mono ds-text-tertiary ds-text-xs"
                        style={{ minWidth: 20 }}
                      >
                        {i + 1}.
                      </span>
                      <Badge tone="accent">{label}</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => moveMember(uid, -1)}
                        disabled={i === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => moveMember(uid, 1)}
                        disabled={i === selected.length - 1}
                      >
                        ↓
                      </Button>
                      <Button
                        type="button"
                        variant="danger-ghost"
                        size="sm"
                        onClick={() => removeMember(uid)}
                      >
                        Remove
                      </Button>
                    </Row>
                  );
                })}
              </Stack>
            )}
          </Stack>

          {available.length > 0 && (
            <Stack gap={2}>
              <span className="ds-label">Add user</span>
              <Row gap={1} align="center">
                {available.map((m) => (
                  <Button
                    key={m.userId}
                    type="button"
                    variant="secondary"
                    size="sm"
                    iconLeft={<Plus size={10} strokeWidth={2} />}
                    onClick={() => addMember(m.userId)}
                  >
                    {m.displayName ?? m.email ?? m.userId}
                  </Button>
                ))}
              </Row>
            </Stack>
          )}

          <Row gap={2} justify="end">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!canSubmit} loading={submitting}>
              {isEdit ? 'Save changes' : 'Create rotation'}
            </Button>
          </Row>
        </Stack>
      </CardBody>
    </Card>
  );
}

/**
 * Default rotation start: next Monday at 09:00 UTC, formatted for a
 * <input type="datetime-local"> field. Most teams roll on Monday
 * morning so this trims clicks.
 */
function defaultStartAtLocalIso(): string {
  const now = new Date();
  // Day-of-week 0=Sun, 1=Mon ... we want Monday.
  const day = now.getUTCDay();
  const daysToAdd = day === 1 ? 7 : (8 - day) % 7;
  const target = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + daysToAdd,
      9,
      0,
      0,
    ),
  );
  // datetime-local wants no zone suffix, format: YYYY-MM-DDTHH:mm
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${target.getUTCFullYear()}-${pad(target.getUTCMonth() + 1)}-${pad(
    target.getUTCDate(),
  )}T${pad(target.getUTCHours())}:${pad(target.getUTCMinutes())}`;
}

/**
 * Convert an ms timestamp to the local-ISO format <input
 * type="datetime-local"> wants (no timezone suffix). Used when
 * pre-filling the edit form with an existing schedule's start time.
 */
function toLocalInputIso(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function formatDatetime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
